import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  FileText, Upload, Trash2, Edit2, X, Download, Search,
  CheckSquare, Play, ClipboardList, Sparkles, BookOpen, Users, CheckCircle, Plus, ChevronRight, CheckCircle2
} from 'lucide-react';

function ProcessDocs({ user, workspaceId }) {
  const [activeTab, setActiveTab] = useState('checklists');
  
  // PDF Documents state (original feature)
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editDocTitle, setEditDocTitle] = useState('');
  const [searchDocQuery, setSearchDocQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [docError, setDocError] = useState('');
  const fileInputRef = useRef(null);

  // Checklist Manifesto state
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runItems, setRunItems] = useState([]);
  const [launchingTemplate, setLaunchingTemplate] = useState(null);
  const [newRunTitle, setNewRunTitle] = useState('');
  
  // Custom Template Creation State
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ title: '', description: '' });
  const [templateItems, setTemplateItems] = useState([
    { phase: 'Pre-Flight (Alignment)', title: '', description: '' }
  ]);
  const [templateError, setTemplateError] = useState('');

  const isAdmin = user?.is_admin;

  // Load PDF Docs
  const loadDocs = useCallback(async () => {
    try {
      const res = await axios.get('/api/docs');
      setDocs(res.data);
    } catch (err) {
      console.error('Failed to load docs:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  // Load Checklist Templates and Active Runs
  const loadChecklists = useCallback(async () => {
    try {
      const [templatesRes, runsRes] = await Promise.all([
        axios.get('/api/checklists/templates'),
        axios.get('/api/checklists/runs')
      ]);
      setTemplates(templatesRes.data);
      setRuns(runsRes.data);
    } catch (err) {
      console.error('Failed to load checklists:', err);
    }
  }, []);

  useEffect(() => {
    loadDocs();
    loadChecklists();
  }, [workspaceId, loadDocs, loadChecklists]);

  // Document actions (original feature)
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadTitle.trim() || !uploadFile) return;
    setUploading(true);
    setDocError('');
    const formData = new FormData();
    formData.append('title', uploadTitle.trim());
    formData.append('file', uploadFile);
    try {
      await axios.post('/api/docs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await loadDocs();
      setShowUpload(false);
      setUploadTitle('');
      setUploadFile(null);
    } catch (err) {
      setDocError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc) => {
    window.open(`/api/docs/${doc.id}/download`, '_blank');
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/docs/${doc.id}`);
      await loadDocs();
    } catch (err) {
      console.error('Failed to delete doc:', err);
    }
  };

  const handleEditDocSave = async (id) => {
    if (!editDocTitle.trim()) return;
    try {
      await axios.put(`/api/docs/${id}`, { title: editDocTitle.trim() });
      await loadDocs();
      setEditingDocId(null);
      setEditDocTitle('');
    } catch (err) {
      console.error('Failed to update doc:', err);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadFile(file);
      if (!uploadTitle) setUploadTitle(file.name.replace(/\.pdf$/i, ''));
    }
  };

  // Checklist Actions
  const handleLaunchChecklist = async (e) => {
    e.preventDefault();
    if (!launchingTemplate || !newRunTitle.trim()) return;
    try {
      const res = await axios.post('/api/checklists/runs', {
        template_id: launchingTemplate.id,
        title: newRunTitle.trim()
      });
      setLaunchingTemplate(null);
      setNewRunTitle('');
      await loadChecklists();
      
      // Open the launched checklist drawer immediately
      handleOpenRunDrawer(res.data);
    } catch (err) {
      alert('Failed to launch checklist run');
    }
  };

  const handleOpenRunDrawer = async (run) => {
    setSelectedRun(run);
    try {
      const res = await axios.get(`/api/checklists/runs/${run.id}/items`);
      setRunItems(res.data);
    } catch (err) {
      console.error('Failed to load checklist items:', err);
    }
  };

  const handleToggleItem = async (item) => {
    if (!selectedRun) return;
    const nextChecked = !item.checked;
    
    // Optimistic UI update
    setRunItems(prev => prev.map(i => {
      if (i.item_id === item.item_id) {
        return {
          ...i,
          checked: nextChecked,
          checked_by: nextChecked ? (user?.name || 'User') : null,
          checked_at: nextChecked ? new Date().toISOString() : null
        };
      }
      return i;
    }));

    try {
      await axios.put(`/api/checklists/runs/${selectedRun.id}/items/${item.item_id}`, {
        checked: nextChecked
      });
      // Silent reload to sync exact times & completed counts
      const runsRes = await axios.get('/api/checklists/runs');
      setRuns(runsRes.data);
      const activeRun = runsRes.data.find(r => r.id === selectedRun.id);
      if (activeRun) {
        setSelectedRun(activeRun);
      }
    } catch (err) {
      console.error('Failed to toggle item:', err);
      // Revert on failure
      handleOpenRunDrawer(selectedRun);
    }
  };

  const handleUpdateHuddle = async (huddleConducted, notes) => {
    if (!selectedRun) return;
    try {
      await axios.put(`/api/checklists/runs/${selectedRun.id}`, {
        huddle_conducted: huddleConducted,
        huddle_notes: notes
      });
      const runsRes = await axios.get('/api/checklists/runs');
      setRuns(runsRes.data);
      const activeRun = runsRes.data.find(r => r.id === selectedRun.id);
      if (activeRun) {
        setSelectedRun(activeRun);
      }
    } catch (err) {
      console.error('Failed to update huddle:', err);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!selectedRun) return;
    if (selectedRun.total_items > selectedRun.checked_items) {
      if (!window.confirm('Some items are not checked yet. Mark this checklist as completed anyway?')) {
        return;
      }
    } else {
      if (!window.confirm('Mark this checklist as completed and close it?')) {
        return;
      }
    }
    try {
      await axios.put(`/api/checklists/runs/${selectedRun.id}`, {
        status: 'completed'
      });
      setSelectedRun(null);
      setRunItems([]);
      await loadChecklists();
    } catch (err) {
      console.error('Failed to complete checklist:', err);
    }
  };

  const handleDeleteRun = async (runId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete/Archive this active checklist run? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/checklists/runs/${runId}`);
      if (selectedRun?.id === runId) {
        setSelectedRun(null);
        setRunItems([]);
      }
      await loadChecklists();
    } catch (err) {
      console.error('Failed to delete checklist run:', err);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setTemplateError('');
    if (!templateForm.title.trim()) {
      setTemplateError('Template title is required');
      return;
    }
    const validItems = templateItems.filter(i => i.title.trim());
    if (validItems.length === 0) {
      setTemplateError('At least one checklist item is required');
      return;
    }

    try {
      await axios.post('/api/checklists/templates', {
        title: templateForm.title.trim(),
        description: templateForm.description.trim(),
        items: validItems
      });
      setTemplateForm({ title: '', description: '' });
      setTemplateItems([{ phase: 'Pre-Flight (Alignment)', title: '', description: '' }]);
      setShowCreateTemplate(false);
      await loadChecklists();
    } catch (err) {
      setTemplateError('Failed to create checklist template');
    }
  };

  const handleAddFormItem = () => {
    setTemplateItems([...templateItems, { phase: 'Pre-Flight (Alignment)', title: '', description: '' }]);
  };

  const handleRemoveFormItem = (index) => {
    const next = [...templateItems];
    next.splice(index, 1);
    setTemplateItems(next);
  };

  const handleUpdateFormItem = (index, field, value) => {
    const next = [...templateItems];
    next[index][field] = value;
    setTemplateItems(next);
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this checklist template? Active checklists launched from this template will remain.')) return;
    try {
      await axios.delete(`/api/checklists/templates/${templateId}`);
      await loadChecklists();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  // Utility helpers
  const formatFileSize = (bytes) => {
    if (!bytes) return '--';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Filtering docs
  const filteredDocs = docs.filter(d =>
    d.title.toLowerCase().includes(searchDocQuery.toLowerCase())
  );

  // Group items by Phase for running checklist
  const groupedItems = runItems.reduce((acc, item) => {
    const phase = item.phase || 'General Essentials';
    if (!acc[phase]) acc[phase] = [];
    acc[phase].push(item);
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Module Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', margin: 0, trackingLetter: '-0.02em' }}>
            Processes & Workflows
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
            Systematize operational execution to secure flawless results and eliminate critical errors.
          </p>
        </div>

        {/* Tab switchers */}
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '12px', padding: '4px', border: '1px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveTab('checklists')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'checklists' ? 'white' : 'transparent',
              color: activeTab === 'checklists' ? '#6366f1' : '#64748b',
              fontWeight: '600',
              fontSize: '0.88rem',
              cursor: 'pointer',
              boxShadow: activeTab === 'checklists' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <CheckSquare size={16} />
            Checklists Manifesto
          </button>
          <button
            onClick={() => setActiveTab('docs')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'docs' ? 'white' : 'transparent',
              color: activeTab === 'docs' ? '#6366f1' : '#64748b',
              fontWeight: '600',
              fontSize: '0.88rem',
              cursor: 'pointer',
              boxShadow: activeTab === 'docs' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FileText size={16} />
            Guides & Handbooks
          </button>
        </div>
      </div>

      {/* Checklist Manifesto Tab content */}
      {activeTab === 'checklists' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
          
          {/* Atul Gawande Quote Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
            border: '1.5px solid #bfdbfe',
            borderRadius: '16px',
            padding: '20px 24px',
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.05)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <BookOpen size={24} />
            </div>
            <div>
              <blockquote style={{ margin: 0, fontStyle: 'italic', color: '#1e3a8a', fontSize: '0.92rem', fontWeight: '500', lineHeight: '1.5' }}>
                "The main reason experts fail isn't ignorance but ineptitude — they know what to do, but slip up under pressure... A well-designed checklist is a humble tool that guards against exactly these avoidable errors."
              </blockquote>
              <cite style={{ display: 'block', marginTop: '6px', fontSize: '0.78rem', color: '#2563eb', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                — Atul Gawande, The Checklist Manifesto
              </cite>
            </div>
          </div>

          {/* Sub-layout for checklists */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', alignItems: 'start' }}>
            
            {/* Left Column: Templates & Creation */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                border: '1.5px solid #cbd5e1',
                padding: '20px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.03)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ClipboardList size={18} color="#6366f1" />
                    Checklist Templates
                  </h3>
                  {isAdmin && (
                    <button
                      onClick={() => setShowCreateTemplate(true)}
                      style={{
                        padding: '6px 12px',
                        background: '#e0e7ff',
                        color: '#4f46e5',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#c7d2fe'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#e0e7ff'}
                    >
                      <Plus size={14} /> New Template
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {templates.map(template => (
                    <div
                      key={template.id}
                      style={{
                        padding: '14px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #cbd5e1',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '12px'
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>{template.title}</span>
                          {isAdmin && template.workspace_id !== null && (
                            <button
                              onClick={(e) => handleDeleteTemplate(template.id, e)}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0 }}
                              title="Delete template"
                            >
                              <Trash2 size={14} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'} />
                            </button>
                          )}
                        </div>
                        <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: '#64748b', lineHeight: '1.4' }}>
                          {template.description}
                        </p>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                        <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '500' }}>
                          {template.item_count} killer items to check
                        </span>
                        
                        <button
                          onClick={() => setLaunchingTemplate(template)}
                          style={{
                            padding: '6px 12px',
                            background: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                          }}
                        >
                          <Play size={11} fill="white" /> Launch Checklist
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Running / Active Checklists */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                border: '1.5px solid #cbd5e1',
                padding: '20px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.03)',
                minHeight: '300px'
              }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sparkles size={18} color="#10b981" />
                  Active Workspace Runs
                </h3>

                {runs.filter(r => r.status === 'active').length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
                    <CheckCircle2 size={40} color="#cbd5e1" style={{ margin: '0 auto 12px' }} />
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem', color: '#64748b' }}>No active runs in this workspace</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Launch one of the templates to start checking off items!</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {runs.filter(r => r.status === 'active').map(run => {
                      const percent = run.total_items > 0 ? Math.round((run.checked_items / run.total_items) * 100) : 0;
                      return (
                        <div
                          key={run.id}
                          onClick={() => handleOpenRunDrawer(run)}
                          style={{
                            padding: '16px',
                            background: selectedRun?.id === run.id ? '#f5f3ff' : '#f8fafc',
                            borderRadius: '12px',
                            border: selectedRun?.id === run.id ? '1.5px solid #6366f1' : '1px solid #cbd5e1',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedRun?.id !== run.id) e.currentTarget.style.borderColor = '#6366f1';
                          }}
                          onMouseLeave={(e) => {
                            if (selectedRun?.id !== run.id) e.currentTarget.style.borderColor = '#cbd5e1';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div>
                              <span style={{ fontWeight: '700', fontSize: '0.98rem', color: '#0f172a' }}>{run.title}</span>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                Based on: <strong style={{ color: '#475569' }}>{run.template_title || 'Custom Template'}</strong>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleDeleteRun(run.id, e)}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0 }}
                              title="Archive run"
                            >
                              <X size={15} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'} />
                            </button>
                          </div>

                          {/* Progress bar */}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', marginBottom: '4px' }}>
                              <span style={{ color: '#64748b', fontWeight: '500' }}>Progress</span>
                              <span style={{ color: '#4f46e5', fontWeight: '700' }}>{run.checked_items}/{run.total_items} checked ({percent}%)</span>
                            </div>
                            <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${percent}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)', borderRadius: '4px', transition: 'width 0.3s ease' }}></div>
                            </div>
                          </div>

                          {/* Team Huddle Indicator */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: run.huddle_conducted ? '#059669' : '#d97706', fontWeight: '600' }}>
                              <Users size={12} />
                              {run.huddle_conducted ? '✓ Team Huddle Done' : '○ Huddle Pending'}
                            </span>
                            
                            <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '2px', fontWeight: '500' }}>
                              Open checklist <ChevronRight size={12} />
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Completed Runs Log */}
                {runs.filter(r => r.status === 'completed').length > 0 && (
                  <div style={{ marginTop: '24px', borderTop: '1.5px solid #cbd5e1', paddingTop: '20px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                      Completed Log / Archives
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {runs.filter(r => r.status === 'completed').map(run => (
                        <div
                          key={run.id}
                          style={{
                            padding: '10px 12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            opacity: 0.8
                          }}
                        >
                          <div>
                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', textDecoration: 'line-through' }}>{run.title}</span>
                            <span style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8' }}>
                              Completed on {new Date(run.completed_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#10b981', fontSize: '0.75rem', fontWeight: '600' }}>
                            <CheckCircle size={12} /> Closed
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
            
          </div>
        </div>
      )}

      {/* Running Checklist Console Drawer */}
      {selectedRun && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '540px',
          maxWidth: '100vw',
          height: '100vh',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '-10px 0 40px rgba(0,0,0,0.12)',
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1.5px solid #cbd5e1',
          animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
        }}>
          
          {/* Drawer Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            background: '#fafafa'
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6366f1', fontWeight: '700' }}>
                Checklist execution
              </span>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', margin: '4px 0 0' }}>
                {selectedRun.title}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                Launched from template: <strong style={{ color: '#475569' }}>{selectedRun.template_title || 'Custom'}</strong>
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setTimeout(() => setSelectedRun(null), 100);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '6px' }}
              title="Close panel"
            >
              <X size={20} />
            </button>
          </div>

          {/* Drawer Scroll Container */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            
            {/* Progress status */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #cbd5e1',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#475569' }}>Completeness</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#4f46e5' }}>
                  {selectedRun.checked_items} / {selectedRun.total_items} checked ({selectedRun.total_items > 0 ? Math.round((selectedRun.checked_items / selectedRun.total_items) * 100) : 0}%)
                </span>
              </div>
              <div style={{ width: '100%', height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  width: `${selectedRun.total_items > 0 ? (selectedRun.checked_items / selectedRun.total_items) * 100 : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>

            {/* Atul Gawande "Team Huddle" Card (Forces communication!) */}
            <div style={{
              background: selectedRun.huddle_conducted ? '#ecfdf5' : '#fffbeb',
              border: selectedRun.huddle_conducted ? '1.5px solid #a7f3d0' : '1.5px solid #fde68a',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Users size={18} color={selectedRun.huddle_conducted ? '#059669' : '#d97706'} />
                  <span style={{ fontWeight: '700', fontSize: '0.9rem', color: selectedRun.huddle_conducted ? '#065f46' : '#92400e' }}>
                    Forced Team Huddle
                  </span>
                </div>
                
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={selectedRun.huddle_conducted}
                    onChange={(e) => handleUpdateHuddle(e.target.checked, selectedRun.huddle_notes || '')}
                    style={{ accentColor: '#10b981', cursor: 'pointer' }}
                  />
                  Huddle Conducted
                </label>
              </div>
              
              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: selectedRun.huddle_conducted ? '#047857' : '#b45309', lineHeight: '1.4' }}>
                Gawande's principles demand team coordination. Gather all members, introduce roles, and surface major risks before ticking the checklists.
              </p>

              <input
                type="text"
                placeholder="List major concerns surfaced during the huddle..."
                value={selectedRun.huddle_notes || ''}
                onChange={(e) => handleUpdateHuddle(selectedRun.huddle_conducted, e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.82rem',
                  color: '#1e293b',
                  background: 'white',
                  outline: 'none'
                }}
              />
            </div>

            {/* Checklist Items Grouped by Phase */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {Object.keys(groupedItems).map(phaseName => (
                <div key={phaseName} style={{ border: '1.5px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', fontWeight: '700', fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {phaseName}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {groupedItems[phaseName].map(item => (
                      <div
                        key={item.item_id}
                        onClick={() => handleToggleItem(item)}
                        style={{
                          padding: '14px 16px',
                          borderBottom: '1px solid #e2e8f0',
                          background: item.checked ? '#f8fafc' : 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'start',
                          gap: '12px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (!item.checked) e.currentTarget.style.background = 'white';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => {}} // toggling handled on click
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: '#10b981',
                            cursor: 'pointer',
                            marginTop: '2px',
                            flexShrink: 0
                          }}
                        />
                        
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{
                            fontWeight: '600',
                            fontSize: '0.88rem',
                            color: item.checked ? '#64748b' : '#0f172a',
                            textDecoration: item.checked ? 'line-through' : 'none'
                          }}>
                            {item.title}
                          </span>
                          
                          {item.description && (
                            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>
                              {item.description}
                            </p>
                          )}

                          {item.checked && item.checked_by && (
                            <span style={{ display: 'block', fontSize: '0.72rem', color: '#10b981', marginTop: '6px', fontWeight: '500' }}>
                              ✓ Checked by {item.checked_by} at {new Date(item.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Drawer Footer controls */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '12px',
            background: '#fafafa'
          }}>
            <button
              onClick={handleCompleteChecklist}
              style={{
                flex: 1,
                padding: '12px',
                background: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px rgba(5, 150, 105, 0.2)',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#047857'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#059669'}
            >
              <CheckCircle size={16} /> Complete & Close Checklist
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setTimeout(() => setSelectedRun(null), 100);
              }}
              style={{
                padding: '12px 20px',
                background: 'white',
                border: '1.5px solid #cbd5e1',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#475569',
                cursor: 'pointer'
              }}
            >
              Minimize
            </button>
          </div>

        </div>
      )}

      {/* Launch Checklist Modal overlay */}
      {launchingTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '400px',
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
          }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>
              🚀 Launch Situational Checklist
            </h4>
            <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#64748b' }}>
              Specify a context or title for this checklist execution (e.g., "Kickoff - Coca-Cola", or "OR Room 3").
            </p>

            <form onSubmit={handleLaunchChecklist}>
              <input
                type="text"
                placeholder="Checklist Instance Title..."
                value={newRunTitle}
                onChange={(e) => setNewRunTitle(e.target.value)}
                autoFocus
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.9rem',
                  marginBottom: '16px',
                  outline: 'none'
                }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setLaunchingTemplate(null)}
                  style={{
                    padding: '8px 16px',
                    background: 'white',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Launch Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Template Creation Drawer / Overlay */}
      {showCreateTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '600px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '28px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800', color: '#0f172a' }}>
                ✨ Design Custom Checklist Template
              </h4>
              <button onClick={() => setShowCreateTemplate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            {templateError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '8px', marginBottom: '14px', fontSize: '0.85rem' }}>{templateError}</div>}

            <form onSubmit={handleCreateTemplate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Template Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Server Migration Checklist"
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Short Description</label>
                  <input
                    type="text"
                    placeholder="Brief summary of when this checklist should be executed"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }}
                  />
                </div>
              </div>

              {/* Items checklist editor */}
              <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#475569' }}>Checklist Killer Items</label>
                  <button
                    type="button"
                    onClick={handleAddFormItem}
                    style={{ padding: '4px 8px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                  >
                    + Add Item
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '4px' }}>
                  {templateItems.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                      <select
                        value={item.phase}
                        onChange={(e) => handleUpdateFormItem(idx, 'phase', e.target.value)}
                        style={{ padding: '8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.8rem' }}
                      >
                        <option value="Pre-Flight (Alignment)">Pre-Flight (Alignment)</option>
                        <option value="In-Flight (Execution)">In-Flight (Execution)</option>
                        <option value="Post-Flight (Review)">Post-Flight (Review)</option>
                      </select>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <input
                          type="text"
                          placeholder="Check item title..."
                          value={item.title}
                          onChange={(e) => handleUpdateFormItem(idx, 'title', e.target.value)}
                          required
                          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '0.85rem' }}
                        />
                        <input
                          type="text"
                          placeholder="Brief description / helper note (optional)..."
                          value={item.description}
                          onChange={(e) => handleUpdateFormItem(idx, 'description', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.75rem', color: '#64748b' }}
                        />
                      </div>
                      {templateItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFormItem(idx)}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '6px' }}
                        >
                          <X size={15} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateTemplate(false)}
                  style={{ padding: '10px 20px', background: 'white', border: '1.5px solid #cbd5e1', borderRadius: '8px', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Docs (Guides & Handbooks) Tab content */}
      {activeTab === 'docs' && (
        <div style={{ background: 'white', border: '1.5px solid #cbd5e1', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} color="#ef4444" />
                Guides & Handbooks
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '4px 0 0' }}>
                Store processes, standards, and instructional manuals in PDF format.
              </p>
            </div>

            {isAdmin && (
              <button
                onClick={() => setShowUpload(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: '#7c3aed',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)'
                }}
              >
                <Upload size={14} /> Upload PDF
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', marginBottom: '16px', maxWidth: '400px' }}>
            <Search size={16} color="#94a3b8" />
            <input
              style={{ border: 'none', outline: 'none', fontSize: '0.88rem', flex: 1, color: '#334155', background: 'transparent' }}
              placeholder="Search documents..."
              value={searchDocQuery}
              onChange={(e) => setSearchDocQuery(e.target.value)}
            />
          </div>

          {loadingDocs ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>Loading handbooks...</div>
          ) : filteredDocs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <FileText size={48} color="#cbd5e1" style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#64748b' }}>
                {searchDocQuery ? 'No documents match your search' : 'No documents yet'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredDocs.map((doc) => (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = '#7c3aed'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onClick={() => handleDownload(doc)}
                >
                  <div style={{ width: '36px', height: '36px', background: '#fef2f2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={18} color="#ef4444" />
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingDocId === doc.id ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleEditDocSave(doc.id); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                      >
                        <input
                          style={{ padding: '6px 10px', border: '1.5px solid #7c3aed', borderRadius: '6px', fontSize: '0.85rem', outline: 'none', width: '100%', maxWidth: '300px' }}
                          value={editDocTitle}
                          onChange={(e) => setEditDocTitle(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Escape') setEditingDocId(null); }}
                        />
                        <button type="submit" style={{ padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>Save</button>
                        <button type="button" onClick={() => setEditingDocId(null)} style={{ padding: '6px 12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                      </form>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.title}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px', display: 'flex', gap: '10px' }}>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>Uploaded {formatDate(doc.updated_at)}</span>
                          {doc.uploader_name && <span>by {doc.uploader_name}</span>}
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleDownload(doc)}
                      style={{ padding: '6px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      title="Download"
                    >
                      <Download size={14} />
                    </button>
                    
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => { setEditingDocId(doc.id); setEditDocTitle(doc.title); }}
                          style={{ padding: '6px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          title="Edit Title"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteDoc(doc)}
                          style={{ padding: '6px', background: 'white', border: '1px solid #fecaca', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Docs Upload Modal */}
      {showUpload && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} onClick={() => { if (!uploading) setShowUpload(false); }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '440px', maxWidth: '90vw', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>Upload Handbook</span>
              <button onClick={() => { if (!uploading) { setShowUpload(false); setDocError(''); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={20} />
              </button>
            </div>

            {docError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '8px', fontSize: '0.8rem', marginBottom: '12px' }}>{docError}</div>}

            <form onSubmit={handleUpload}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>Document Title</label>
                <input
                  placeholder="e.g. Design System Handbook"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  disabled={uploading}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>PDF File</label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${dragOver ? '#7c3aed' : '#cbd5e1'}`,
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: dragOver ? '#f5f3ff' : '#f8fafc',
                    transition: 'all 0.15s'
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadFile(file);
                        if (!uploadTitle) setUploadTitle(file.name.replace(/\.pdf$/i, ''));
                      }
                    }}
                  />
                  {uploadFile ? (
                    <div>
                      <FileText size={28} color="#7c3aed" style={{ margin: '0 auto 8px' }} />
                      <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '0.85rem' }}>{uploadFile.name}</p>
                      <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '0.75rem' }}>{formatFileSize(uploadFile.size)}</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={28} color="#94a3b8" style={{ margin: '0 auto 8px' }} />
                      <p style={{ margin: 0, color: '#475569', fontSize: '0.85rem', fontWeight: '500' }}>
                        Drag & drop PDF here, or <span style={{ color: '#7c3aed', fontWeight: '700' }}>browse</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading || !uploadTitle.trim() || !uploadFile}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: (uploading || !uploadTitle.trim() || !uploadFile) ? '#cbd5e1' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  cursor: (uploading || !uploadTitle.trim() || !uploadFile) ? 'not-allowed' : 'pointer'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default ProcessDocs;
